import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import { AxiosError } from 'axios';
import { useAppStore } from '../stores/appStore';
import amplinthLogo from '../assets/images/amplinthLogo-new.jpeg';
import { userAuthApi, UserCreate } from '../lib/userAuthApi';

interface FieldErrors {
  full_name?: string;
  email?: string;
  password?: string;
  confirm_password?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function splitFullName(name: string): { first_name: string; last_name: string } {
  const trimmed = name.trim().replace(/\s+/g, ' ');
  const parts = trimmed.split(' ');
  if (parts.length === 1) return { first_name: parts[0], last_name: parts[0] };
  const first_name = parts.slice(0, -1).join(' ');
  const last_name = parts[parts.length - 1];
  return { first_name, last_name };
}

export default function Register() {
  const navigate = useNavigate();
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    const trimmedName = fullName.trim();

    if (!trimmedName) {
      next.full_name = 'Required';
    } else if (trimmedName.length > 200) {
      next.full_name = 'Must be 200 characters or fewer';
    } else if (!trimmedName.includes(' ')) {
      next.full_name = 'Please enter your first and last name';
    }

    if (!email.trim()) next.email = 'Required';
    else if (!EMAIL_REGEX.test(email.trim())) next.email = 'Enter a valid email address';

    if (!password) next.password = 'Required';
    else if (password.length < 8) next.password = 'Password must be at least 8 characters';

    if (!confirmPassword) next.confirm_password = 'Required';
    else if (confirmPassword !== password) next.confirm_password = 'Passwords do not match';

    return next;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const { first_name, last_name } = splitFullName(fullName);
      const payload: UserCreate = {
        first_name,
        last_name,
        email: email.trim().toLowerCase(),
        password,
      };

      await userAuthApi.register(payload);

      toast.success('Account created. Please sign in to continue.');
      navigate('/login');
    } catch (err) {
      const axiosErr = err as AxiosError<{
        detail?: string | Array<{ loc: (string | number)[]; msg: string }>;
      }>;
      const status = axiosErr.response?.status;
      const detail = axiosErr.response?.data?.detail;

      if (status === 400 && typeof detail === 'string') {
        if (detail.toLowerCase().includes('email')) {
          setErrors((prev) => ({
            ...prev,
            email: 'An account with this email already exists.',
          }));
        } else {
          toast.error(detail);
        }
      } else if (status === 422 && Array.isArray(detail)) {
        const fieldErrors: FieldErrors = {};
        for (const item of detail) {
          const field = item.loc[item.loc.length - 1];
          if (field === 'email') fieldErrors.email = 'Enter a valid email address';
          else if (field === 'password') fieldErrors.password = 'Password must be at least 8 characters';
          else if (field === 'first_name' || field === 'last_name') fieldErrors.full_name = 'Required';
        }
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputBase =
    'w-full px-4 py-3 bg-white border rounded-lg text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent transition-all';
  const inputOk = 'border-neutral-200 hover:border-neutral-300';
  const inputBad = 'border-red-300 focus:ring-red-500';

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-secondary-50 via-white to-secondary-100 flex items-center justify-center p-4">
      {/* Decorative background shapes */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute top-[8%] left-[6%] h-72 w-72 rounded-full border border-secondary-200/60" />
        <div className="absolute top-[55%] left-[2%] h-40 w-40 rounded-full border border-secondary-200/50" />
        <div className="absolute top-[78%] left-[18%] h-24 w-24 rounded-full bg-secondary-100/60" />
        <div className="absolute top-[14%] left-[22%] h-12 w-12 rounded-xl bg-secondary-100/70" />
        <div className="absolute top-[40%] left-[14%] h-3 w-3 rounded-full bg-secondary-300/70" />
        <div className="absolute top-[68%] left-[30%] h-2 w-2 rounded-full bg-secondary-400/60" />

        <div className="absolute top-[10%] right-[8%] h-16 w-16 rounded-2xl bg-secondary-100/60" />
        <div className="absolute top-[30%] right-[4%] h-56 w-56 rounded-full border border-secondary-200/60" />
        <div className="absolute top-[62%] right-[14%] h-32 w-32 rounded-full border border-secondary-200/50" />
        <div className="absolute top-[82%] right-[6%] h-20 w-20 rounded-full bg-secondary-100/60" />
        <div className="absolute top-[22%] right-[24%] h-2 w-2 rounded-full bg-secondary-300/70" />
        <div className="absolute top-[78%] right-[28%] h-3 w-3 rounded-full bg-secondary-400/60" />
      </div>

      {/* Card */}
      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl shadow-secondary-200/40 p-8 sm:p-10">
          <div className="flex justify-center mb-6">
            <img src={amplinthLogo} alt="Amplinth" className="h-12 w-auto" />
          </div>

          <h1 className="text-2xl font-bold text-neutral-900 mb-1">Create Account</h1>
          <p className="text-sm text-neutral-500 mb-6">
            Your Platform for Enterprise Value and Financial Clarity.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label htmlFor="fullName" className="block text-sm font-semibold text-neutral-800 mb-1.5">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                id="fullName"
                type="text"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className={`${inputBase} ${errors.full_name ? inputBad : inputOk}`}
                autoComplete="name"
                autoFocus
                maxLength={200}
              />
              {errors.full_name && <p className="mt-1.5 text-sm text-red-600">{errors.full_name}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-neutral-800 mb-1.5">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                placeholder="john@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`${inputBase} ${errors.email ? inputBad : inputOk}`}
                autoComplete="email"
              />
              {errors.email && <p className="mt-1.5 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-neutral-800 mb-1.5">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Minimum 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputBase} pr-11 ${errors.password ? inputBad : inputOk}`}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-700 hover:text-neutral-900 focus:outline-none"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && <p className="mt-1.5 text-sm text-red-600">{errors.password}</p>}
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-semibold text-neutral-800 mb-1.5"
              >
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`${inputBase} pr-11 ${errors.confirm_password ? inputBad : inputOk}`}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-700 hover:text-neutral-900 focus:outline-none"
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirm_password && (
                <p className="mt-1.5 text-sm text-red-600">{errors.confirm_password}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-6 px-6 py-3.5 rounded-xl text-white font-semibold bg-gradient-to-br from-secondary-500 to-secondary-800 hover:from-secondary-600 hover:to-secondary-900 shadow-lg shadow-secondary-500/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting && (
                <svg
                  className="w-5 h-5 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              )}
              <span>{isSubmitting ? 'Creating account…' : 'Create Account'}</span>
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-neutral-600">
            Already have an account?{' '}
            <Link to="/login" className="text-secondary-700 hover:text-secondary-800 font-semibold">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
